import AddIcon from '@mui/icons-material/Add';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import FormActionButtons from '../components/FormActionButtons';
import SaveIcon from '@mui/icons-material/Save';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { TimePicker } from '../components/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { QRCodeCanvas } from 'qrcode.react';
import React from 'react';
import PropTypes from 'prop-types';

import { ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import FormGroup from '@mui/material/FormGroup';
import AdvanecdReviewWidget from '../analytics/AdvanecdReviewWidget';
import { DataAPIContext, CREATE, DELETE, UPDATE } from '../api';
import BlockRedirect from '../components/BlockRedirect';
import FixedLinearProgress from '../components/FixedLinearProgress';
import LocalizedTextSetField from '../components/LocalizedTextField';
import PageMeta from '../components/PageMeta';
import QuillEditor from '../components/QuillEditor';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import SystemTagsDropdown from '../components/SystemTagsDropdown';
import TabPanel, { a11yProps } from '../components/TabPanel';
import TimezonePicker from '../components/TimezonePicker';
import FormLicenseeContext from '../components/FormLicenseeContext';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import SupportContact from './GeneralSupportContact';
import TripDefaultSettings from '../components/TripDefaultSettings';
import useGeneralSettings, { TIME_FORMAT, EMPTY_ARRAY, DAYS } from './useGeneralSettings';

// CP-8403: "Direct to link" applies to both review widgets; only the page it skips differs
const positiveFeedbackHelp = (directLink, advanced) => {
  if (advanced) {
    return directLink
      ? "A positive rating sends the guest straight to the trip's review destination, skipping the positive feedback pages. Trips without one still show those pages."
      : "A positive rating shows the positive feedback pages, and the last page's button leads to the trip's review destination.";
  }
  return directLink
    ? "A positive rating sends the guest straight to the trip's review destination. Trips without one still show this page."
    : "A positive rating shows this page, and its button leads to the trip's review destination.";
};

/** =========================
 *  Styles
 *  ======================= */
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    paddingBottom: 0,
    backgroundColor: '#FCFCFE',
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0),
    },
  },
  innerGridRoot: {
    overflowY: 'auto',
    // 64px top nav + ~32px title + ~48px tabs + ~56px button panel + 16px padding
    maxHeight: 'calc(100vh - 216px)',
    [theme.breakpoints.down('sm')]: {
      maxHeight: 'calc(100vh - 260px)',
    },
  },
  buttonPanel: {
    backgroundColor: grey[100],
    zIndex: 10,
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: theme.spacing(8),
    },
  },
  tabSelectButtons: {
    border: 'solid 1px #D8D8D8',
    margin: '0 1rem 0rem 1rem !important',
    borderRadius: '12px 12px 0px 0px',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.down('sm')]: {
      margin: '0 !important',
    },
  },
  sectionCard: {
    border: 'solid 1px #D8D8D8',
    backgroundColor: 'white',
    borderRadius: '12px',
    margin: '1rem',
  },
}));

/** =========================
 *  AutoReplyField (isolated, fast, save-safe)
 *  ======================= */
const AutoReplyField = React.memo(
  React.forwardRef(function AutoReplyField({ value, onCommit, disabled, onDirtyChange }, ref) {
    const [draft, setDraft] = React.useState(value ?? "");

    // keep local with parent
    React.useEffect(() => { setDraft(value ?? ""); }, [value]);

    const isDirty = React.useMemo(() => (value ?? "") !== draft, [value, draft]);
    React.useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    const commit = React.useCallback(() => {
      if ((value ?? "") !== draft) onCommit(draft);
    }, [draft, value, onCommit]);

    React.useImperativeHandle(ref, () => ({
      flush: () => commit(),
      getDraft: () => draft,
      isDirty: () => isDirty,
    }), [commit, draft, isDirty]);

    return (
      <LocalizedTextSetField
        disabled={disabled}
        variant="filled"
        label="Auto-response message"
        value={draft}
        fullWidth
        onChange={(ev) => setDraft(ev.target.value)}
        onBlur={commit}
        containerDirectionOverride="row"
      />
    );
  })
);

AutoReplyField.propTypes = {
  value: PropTypes.any,
  onCommit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onDirtyChange: PropTypes.func,
};

/** =========================
 *  OfficeHoursForm (pure + memoized)
 *  ======================= */
const OfficeHoursForm = React.memo(function OfficeHoursForm(props) {
  const {
    theme,
    hasWritePerm,
    isSmallScreen,
    tour_operator_office_hours_set,
    onTourOperatorOfficeHoursChange,
    rowsWithNoDaysWarning,
  } = props;

  const zeroTime = React.useCallback(() => moment('00:00:00', TIME_FORMAT), []);

  const rows = React.useMemo(
    () =>
      (tour_operator_office_hours_set ?? []).map((oh) => ({
        startTime: oh.office_hours_start_time ? moment(oh.office_hours_start_time, TIME_FORMAT) : null,
        endTime: oh.office_hours_end_time ? moment(oh.office_hours_end_time, TIME_FORMAT) : null,
        timezone: oh.timezone || null,
        selectedDays: [
          oh.day_of_week_mon && 'Mon',
          oh.day_of_week_tue && 'Tue',
          oh.day_of_week_wed && 'Wed',
          oh.day_of_week_thu && 'Thu',
          oh.day_of_week_fri && 'Fri',
          oh.day_of_week_sat && 'Sat',
          oh.day_of_week_sun && 'Sun',
        ].filter(Boolean),
      })),
    [tour_operator_office_hours_set]
  );

  const handleChange = React.useCallback(
    (index, field, value) => {
      const updated = rows.map((r, i) => (i === index ? { ...r, [field]: value } : r));
      onTourOperatorOfficeHoursChange(updated);
    },
    [rows, onTourOperatorOfficeHoursChange]
  );

  const handleAddRow = React.useCallback(() => {
    const updated = [
      ...rows,
      { startTime: null, endTime: null, timezone: null, selectedDays: [] },
    ];
    onTourOperatorOfficeHoursChange(updated);
  }, [rows, onTourOperatorOfficeHoursChange]);

  const handleDeleteRow = React.useCallback(
    (index) => {
      const updated = rows.filter((_, i) => i !== index);
      onTourOperatorOfficeHoursChange(updated);
    },
    [rows, onTourOperatorOfficeHoursChange]
  );

  return (
    <>
      {rows.map((row, index) => (
        <Grid key={index} item xs={12} sm={12} container direction="row" spacing={2} style={{ minWidth: !isSmallScreen ? '975px' : null }}>
          <Grid item xs={1.75} style={{ minWidth: '150px', maxWidth: '240px' }}>
            <TimePicker
              disabled={!hasWritePerm}
              label="Start Time"
              value={row.startTime}
              onChange={(time) => handleChange(index, 'startTime', time)}
              slotProps={{
                textField: { variant: 'filled', size: 'small', fullWidth: true, required: true },
              }}
            />
          </Grid>

          <Grid item xs={0.3} container alignItems="center" justifyContent="center">
            <Typography>&mdash;</Typography>
          </Grid>

          <Grid item xs={1.75} style={{ minWidth: '150px', maxWidth: '240px' }}>
            <TimePicker
              disabled={!hasWritePerm}
              label="End Time"
              value={row.endTime}
              onChange={(time) => handleChange(index, 'endTime', time)}
              slotProps={{
                textField: { variant: 'filled', size: 'small', fullWidth: true, required: true },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2} container alignItems="center" justifyContent="center" style={{ paddingTop: 16, paddingLeft: !isSmallScreen ? 20 : null, minWidth: '150px', maxWidth: !isSmallScreen ? '240px' : null }}>
            <TimezonePicker
              label="Timezone"
              disabled={!hasWritePerm}
              required
              size="small"
              variant="filled"
              value={row.timezone}
              onChange={(ev, nv) => handleChange(index, 'timezone', nv?.value)}
              showTimezoneOffset
              style={{ width: "100%" }}
            />
          </Grid>

          <Grid item xs={12} sm={3} style={{ paddingLeft: !isSmallScreen ? 36 : null, minWidth: !isSmallScreen ? '450px' : null }}>
            <ToggleButtonGroup
              color="primary"
              value={row.selectedDays}
              onChange={(_, newDays) => handleChange(index, 'selectedDays', newDays)}
              aria-label="days of the week"
              size="small"
              style={{ gap: !isSmallScreen ? '0.6rem' : null }}
            >
              {DAYS.map((day) => (
                <ToggleButton
                  key={day}
                  value={day}
                  aria-label={day}
                  style={{
                    height: '2.8rem',
                    width: '2.8rem',
                    borderRadius: '50%',
                    borderLeft: `1px solid ${(theme?.vars || theme)?.palette?.action?.disabledBackground}`,
                  }}
                >
                  {day}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} sm={.5} container alignItems="center" justifyContent="center">
            {rows.length > 0 && (
              <DeleteIconButton allowed={hasWritePerm} onClick={() => handleDeleteRow(index)} />
            )}
          </Grid>
          {rowsWithNoDaysWarning?.includes(index) && row.selectedDays.length === 0 && (
            <Grid item xs={12} style={{ paddingLeft: '1rem', paddingTop: '0.5rem' }}>
              <Typography variant="body2" color="error" style={{ fontStyle: 'italic' }}>
                Warning: No days selected for this office hours entry
              </Typography>
            </Grid>
          )}
        </Grid>
      ))}
      <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddRow} style={{ marginTop: '1rem', marginLeft: '1rem' }}>
        Add Row
      </Button>
    </>
  );
});

OfficeHoursForm.propTypes = {
  theme: PropTypes.shape({
    vars: PropTypes.object,
    palette: PropTypes.shape({
      action: PropTypes.shape({
        disabledBackground: PropTypes.any,
      }),
    }),
  }),
  hasWritePerm: PropTypes.bool,
  isSmallScreen: PropTypes.bool,
  tour_operator_office_hours_set: PropTypes.array,
  onTourOperatorOfficeHoursChange: PropTypes.func.isRequired,
  rowsWithNoDaysWarning: PropTypes.array,
};

/** =========================
 *  TourOperatorSignature (as before)
 *  ======================= */
const TourOperatorSignature = React.memo(function TourOperatorSignature(props) {
  const [signature, setSignature] = React.useState(props.data);
  const [loading, setLoading] = React.useState(false);
  const dataAPI = React.useContext(DataAPIContext);
  const resource = 'tour_operator_signature';
  const { enqueueSnackbar } = useSnackbar();
  const originalSignatureState = props.data;

  const handleEditorChange = (content) => {
    setSignature((prev) => ({ ...prev, text: content }));
  };

  const handleNameChange = (ev) => {
    setSignature((prev) => ({ ...prev, name: ev.target.value }));
  };

  const handleSignatureDelete = async (idx) => {
    if (!signature.id) {
      props.updateTourOperatorSignatures(idx);
      return;
    }
    setLoading(true);
    await dataAPI(DELETE, resource, { id: signature.id })
      .then(() => {
        enqueueSnackbar('Signature deleted', { variant: 'success' });
      })
      .catch((err) => {
        enqueueSnackbar('Error deleting signature', { variant: 'error' });
        console.log('error saving settings', err);
      })
      .finally(() => {
        setLoading(false);
      });

    props.updateTourOperatorSignatures(idx);
  };

  const saveSignaturesChanges = async () => {
    setLoading(true);
    let action = signature.id ? UPDATE : CREATE;
    await dataAPI(action, resource, { id: signature.id, data: signature })
      .then((res) => {
        enqueueSnackbar('Signature saved', { variant: 'success' });
        props.updateTourOperatorSignatures(signature.idx, res.data);
        setSignature(res.data);
      })
      .catch((err) => {
        enqueueSnackbar('Error saving signature', { variant: 'error' });
        console.log('error saving settings', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isChanged = JSON.stringify(originalSignatureState) !== JSON.stringify(signature);

  return (
    <Grid item xs={12} container mb={1}>
      <Grid item xs={12} container justifyContent="space-between" alignItems="center" spacing={1} wrap="nowrap">
        <Grid item xs>
          <TextField disabled={props.disabled} variant="filled" size="small" type="url" label="Signature name" fullWidth value={signature.name} name="name" onChange={handleNameChange} />
        </Grid>
        <Grid item xs="auto">
          {/* CP-8423: [DELETE] [SAVE] right-aligned side by side. CP-5545: DELETE is outlined (never filled)
              and stays visible, disabled, without write access. */}
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
            {props.isSmallScreen ? (
              <DeleteIconButton disabled={props.disabled} onClick={() => handleSignatureDelete(signature.idx)} />
            ) : (
              <DeleteButton size="small" disabled={props.disabled || (props.signatureList.filter((sig) => sig.created).length > 0 && !signature.created)} onClick={() => handleSignatureDelete(signature.idx)}>
                Delete
              </DeleteButton>
            )}
            {loading ? (
              <CircularProgress color="primary" size={20} />
            ) : (
              !props.disabled &&
              (props.isSmallScreen ? (
                <SaveIcon color="primary" disabled={!isChanged} style={{ cursor: 'pointer' }} onClick={() => saveSignaturesChanges(signature.idx)} />
              ) : (
                <Button disabled={!isChanged} size="small" variant="contained" onClick={() => saveSignaturesChanges(signature.idx)}>
                  <SaveIcon />
                  &nbsp;&nbsp;Save
                </Button>
              ))
            )}
          </Box>
        </Grid>
      </Grid>
      <Grid item xs={12} pt={1}>
        {props.disabled ? (
          <TextField disabled={true} fullWidth variant="filled" size="small" multiline value={signature.text} style={{ maxHeight: "200px", overflow: "scroll" }} />
        ) : (
          <QuillEditor
            value={signature.text}
            onChange={handleEditorChange}
            style={{ width: '100%', minHeight: '300px' }}
          />
        )}
      </Grid>
    </Grid>
  );
});

TourOperatorSignature.propTypes = {
  data: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  isSmallScreen: PropTypes.bool,
  signatureList: PropTypes.array.isRequired,
  updateTourOperatorSignatures: PropTypes.func.isRequired,
};

/** =========================
 *  Tab indices
 *  ======================= */
const TAB_BASIC_INFO = 0;
const TAB_OFFICE_HOURS = 1;
const TAB_SUPPORT_CONTACTS = 2;
const TAB_TRIP_DEFAULTS = 3;
const TAB_REVIEWS = 4;
const TAB_CHAT = 5;
const TAB_LICENSEES = 6;

const TAB_SLUGS = {
  'basic-info': TAB_BASIC_INFO,
  'office-hours': TAB_OFFICE_HOURS,
  'support-contacts': TAB_SUPPORT_CONTACTS,
  'trip-defaults': TAB_TRIP_DEFAULTS,
  'reviews': TAB_REVIEWS,
  'chat': TAB_CHAT,
  'licensees': TAB_LICENSEES,
};
const TAB_INDEX_TO_SLUG = Object.fromEntries(Object.entries(TAB_SLUGS).map(([k, v]) => [v, k]));

// Browser tab titles. The tabs are URL-addressable, so the page title names the
// active one rather than a bare "Settings" that never changes (CP-7101).
const TAB_TITLES = {
  [TAB_BASIC_INFO]: 'Settings - Basic Info',
  [TAB_OFFICE_HOURS]: 'Settings - Office Hours',
  [TAB_SUPPORT_CONTACTS]: 'Settings - Support Contacts',
  [TAB_TRIP_DEFAULTS]: 'Settings - Trip Defaults',
  [TAB_REVIEWS]: 'Settings - Reviews',
  [TAB_CHAT]: 'Settings - Chat',
  [TAB_LICENSEES]: 'Settings - Licensees',
};

/** =========================
 *  GeneralSettingsTabs
 *  ======================= */
const GeneralSettingsTabs = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const isSmallScreen = !useIsWidthUp('sm');
  const { hasPerm, isFranchise, currentLicensee, licensees, tourOperator } = React.useContext(ContextStore);

  // Franchise: derive effective TO from sidebar licensee selector
  const selectedLicensee = currentLicensee || null;
  const effectiveTopId = (isFranchise && selectedLicensee) ? selectedLicensee : tourOperator.id;
  const effectiveTopName = isFranchise
    ? (licensees.find(l => l.id === selectedLicensee)?.name || tourOperator.name)
    : null;
  const tabSlug = (location.pathname.replace(/\/$/, '').split('/').pop() || '').toLowerCase();
  const tabValue = TAB_SLUGS[tabSlug] ?? TAB_BASIC_INFO;

  const setTabValue = (newIndex) => {
    const slug = TAB_INDEX_TO_SLUG[newIndex] || 'basic-info';
    history.replace(`/settings/general/${slug}`);
  };

  // Redirect bare /settings/general to /settings/general/basic-info
  React.useEffect(() => {
    if (!TAB_SLUGS[tabSlug] || tabSlug === 'licensees') {
      history.replace(`/settings/general/basic-info`);
    }
  }, []);

  const {
    top, setTop, dirty, syncActive, loadedTop,
    tourOperatorSignatures, setTourOperatorSignatures,
    clientErrors, setClientErrors,
    openChangeHistory, setOpenChangeHistory,
    hasWritePerm, hasReviewsWritePerm,
    setTopAndDirty, onSave, onCancel,
    onSubfieldChange, onChange, onLocalizedTextSetChange,
    onSupportContactAdd, onSupportContactChange, onSupportContactDelete,
    handleSignatureCreate, updateTourOperatorSignatures,
    onTourOperatorOfficeHoursChange,
    autoReplyRef, autoReplyDirty, setAutoReplyDirty,
    rowsWithNoDaysWarning, setRowsWithNoDaysWarning,
    hasFormChanges, officeHoursValid, officeHoursChanged,
    handleOnBlur, handleEmailBlur, handleURLOnBlur,
    handleReviewChange, checkValidURL,
    onReviewLowHighThresholdChange, onReviewRatingSystemChange, onReviewWidgetTypeChange,
    onSystemTagsChange,
    onDefaultLocationSharingChange, onDefaultMyTripsDisplayChange,
    onDefaultHasGroupChatChange,
    onSetDefaultsAssociatedTagCategories,
    getTourOperatorUpdateData,
    review_widget_type, review_rating_system, review_threshold,
    ratingOptions, renderRatingValue,
  } = useGeneralSettings(effectiveTopId);

  /** Memoized support contact list */
  const supportContactElements = React.useMemo(() =>
    (top.support_contacts || []).map((c, idx, list) => (
      <SupportContact
        key={idx}
        idx={idx}
        isLast={idx === list.length - 1}
        data={c}
        onChange={onSupportContactChange}
        onDelete={onSupportContactDelete}
        disabled={!hasWritePerm}
      />
    )), [top.support_contacts, onSupportContactChange, onSupportContactDelete, hasWritePerm]);

  const handleTabChange = React.useCallback((_, newValue) => {
    setTabValue(newValue);
  }, []);

  return (
    <FormLicenseeContext.Provider value={effectiveTopId}>
      <BlockRedirect shouldBlock={hasFormChanges} message={'Are you sure you want to leave without saving changes?'} />
      <div className={classes.root}>
        {openChangeHistory && (
          <Dialog open={openChangeHistory} onClose={() => setOpenChangeHistory(false)} fullWidth={true} maxWidth="xl">
            <DialogTitle>Change history for tour operator &quot;{top.name}&quot;</DialogTitle>
            <DialogContent>
              <RevisionHistoryDialog open={openChangeHistory} resource="tour_operator" id={top.id} />
            </DialogContent>
            <DialogActions>
              <Button variant="contained" color="primary" onClick={() => setOpenChangeHistory(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        )}

        <PageMeta title={TAB_TITLES[tabValue] || 'Settings'} />
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <FixedLinearProgress visibility={syncActive} />

          <Typography variant="h6">
            Settings{effectiveTopName ? `: ${effectiveTopName}` : ''}
          </Typography>

          <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                aria-label="General settings tabs"
              >
                <Tab label="BASIC INFO" {...a11yProps(TAB_BASIC_INFO)} />
                <Tab label="OFFICE HOURS" {...a11yProps(TAB_OFFICE_HOURS)} />
                <Tab label="SUPPORT CONTACTS" {...a11yProps(TAB_SUPPORT_CONTACTS)} />
                <Tab label="TRIP DEFAULTS" {...a11yProps(TAB_TRIP_DEFAULTS)} />
                <Tab label="REVIEWS" {...a11yProps(TAB_REVIEWS)} />
                <Tab label="CHAT" {...a11yProps(TAB_CHAT)} />
              </Tabs>

          <div className={classes.innerGridRoot}>

              {/* ==============================
                  Tab 0 - Basic Info
                  ============================== */}
              <TabPanel value={tabValue} index={TAB_BASIC_INFO} buttonBoxSpacing>
                <Grid container direction="row" p={2} spacing={2} className={classes.sectionCard}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Basic Info</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={!hasWritePerm}
                      variant="filled"
                      size="small"
                      label="Operator Name"
                      fullWidth
                      value={top.name}
                      required
                      name="name"
                      onChange={onChange}
                      InputLabelProps={{ shrink: true }}
                      onBlur={handleOnBlur}
                      error={clientErrors.name}
                      helperText={clientErrors.name && 'Operator name is required'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={!hasWritePerm}
                      variant="filled"
                      size="small"
                      type="email"
                      label="Operator Email"
                      fullWidth
                      value={top.email}
                      name="email"
                      onChange={onChange}
                      InputLabelProps={{ shrink: true }}
                      onBlur={() => handleEmailBlur(top.email)}
                      error={clientErrors.operatorEmail}
                      helperText={clientErrors.operatorEmail && 'Please enter a valid email address'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={!hasWritePerm}
                      variant="filled"
                      size="small"
                      type="url"
                      label="Operator Website"
                      fullWidth
                      value={top.url}
                      name="url"
                      onChange={onChange}
                      InputLabelProps={{ shrink: true }}
                      onBlur={() => handleURLOnBlur(top.url)}
                      error={clientErrors.websiteUrl}
                      helperText={clientErrors.websiteUrl && 'Please enter a valid URL'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={!hasWritePerm}
                      variant="filled"
                      size="small"
                      type="url"
                      label="Default Booking URL"
                      fullWidth
                      value={top.payment_url}
                      name="payment_url"
                      onChange={onChange}
                      InputLabelProps={{ shrink: true }}
                      onBlur={() => handleURLOnBlur(top.payment_url)}
                      error={clientErrors.bookingUrl}
                      helperText={clientErrors.bookingUrl && 'Please enter a valid URL'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SystemTagsDropdown value={top.tags} disabled={!hasWritePerm} onChange={onSystemTagsChange} />
                  </Grid>

                  {top.master_sr_link ? (
                    <>
                      <Grid item xs={12}>
                        <Typography fontWeight={500}>Master self-registration link</Typography>
                      </Grid>
                      <Grid item xs={12} ml={-1.5} mt={-2}>
                        <Link href={top.master_sr_link} target="_blank">
                          <QRCodeCanvas
                            value={top.master_sr_link}
                            includeMargin={true}
                            style={{
                              width: '128px',
                              height: '128px',
                            }}
                          />
                        </Link>
                      </Grid>
                    </>
                  ) : null}

                  <Grid item xs={12}>
                    <Typography fontWeight={500}>Email Signatures</Typography>
                  </Grid>
                  {tourOperatorSignatures.length > 0 ? (
                    tourOperatorSignatures.map((signature) => (
                      <TourOperatorSignature
                        key={signature.idx}
                        disabled={!hasWritePerm}
                        signatureList={tourOperatorSignatures}
                        data={signature}
                        updateTourOperatorSignatures={updateTourOperatorSignatures}
                        isSmallScreen={isSmallScreen}
                      />
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Typography>No email signatures found.</Typography>
                    </Grid>
                  )}
                  {tourOperatorSignatures.filter((sig) => sig.created).length === 0 && hasWritePerm && (
                    <Grid item xs={12}>
                      <Button variant="outlined" onClick={handleSignatureCreate}>
                        <AddIcon fontSize="small"/>&nbsp;Add Signature
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>

              {/* ==============================
                  Tab 1 - Office Hours
                  ============================== */}
              <TabPanel value={tabValue} index={TAB_OFFICE_HOURS} buttonBoxSpacing>
                <Grid container direction="row" p={2} spacing={2} className={classes.sectionCard}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Office Hours</Typography>
                  </Grid>
                  <OfficeHoursForm
                    theme={theme}
                    hasWritePerm={hasWritePerm}
                    isSmallScreen={isSmallScreen}
                    onTourOperatorOfficeHoursChange={onTourOperatorOfficeHoursChange}
                    tour_operator_office_hours_set={top.tour_operator_office_hours_set ?? EMPTY_ARRAY}
                    rowsWithNoDaysWarning={rowsWithNoDaysWarning}
                  />
                  <Grid item xs={12} sm={12} container direction="row">
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox />}
                        label="Post auto-reply outside of office hours"
                        checked={top.outside_office_hours_auto_reply?.enabled || false}
                        name="outside_office_hours_auto_reply.enabled"
                        onChange={(ev) => onSubfieldChange('outside_office_hours_auto_reply.enabled', ev.target.checked)}
                      />
                    </FormGroup>
                    <Grid item xs={12} sm={12} container direction="column" style={{ flexWrap: 'wrap', gap: '1rem' }}>
                      <AutoReplyField
                        ref={autoReplyRef}
                        disabled={!hasWritePerm}
                        value={top.outside_office_hours_auto_reply?.auto_reply_message_text || ""}
                        onCommit={(newVal) =>
                          setTopAndDirty(prev => ({
                            ...prev,
                            outside_office_hours_auto_reply: {
                              ...(prev.outside_office_hours_auto_reply || {}),
                              auto_reply_message_text: newVal,
                            },
                          }))
                        }
                        onDirtyChange={setAutoReplyDirty}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* ==============================
                  Tab 2 - Support Contacts
                  ============================== */}
              <TabPanel value={tabValue} index={TAB_SUPPORT_CONTACTS} buttonBoxSpacing>
                <Grid container direction="row" p={2} spacing={2} className={classes.sectionCard}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Support Contacts</Typography>
                  </Grid>
                  {supportContactElements}
                  {hasWritePerm && (
                    <Grid item xs={12} container>
                      <Button variant="outlined" color="primary" onClick={onSupportContactAdd} disabled={syncActive}>
                        <AddIcon />
                        ADD CONTACT
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>

              {/* ==============================
                  Tab 3 - Default Trip Settings
                  ============================== */}
              <TabPanel value={tabValue} index={TAB_TRIP_DEFAULTS} buttonBoxSpacing>
                <Grid container direction="row" p={2} spacing={2} className={classes.sectionCard}>
                  <Grid item xs={12}>
                    <Typography variant="h6" fontSize={20}>Default Trip Settings</Typography>
                  </Grid>
                  <TripDefaultSettings
                    mode="operator"
                    hasWritePerm={hasWritePerm}
                    hasUpdateTagCategoriesPerm={true}
                    locationSharingValue={top.config?.defaults?.location_sharing}
                    onLocationSharingChange={onDefaultLocationSharingChange}
                    myTripsDisplayValue={!!top.config?.defaults?.mytrips_display}
                    onMyTripsDisplayChange={onDefaultMyTripsDisplayChange}
                    hasGroupChatValue={!!(top.config?.defaults?.has_group_chat || top.config?.defaults?.chat)}
                    onHasGroupChatChange={onDefaultHasGroupChatChange}
                    defaultsAssociatedTagCategories={top.default_tag_categories ?? EMPTY_ARRAY}
                    onSetDefaultsAssociated={onSetDefaultsAssociatedTagCategories}
                    // sdtc WP-E: a sandbox's own operator only (the backend syncs no licensee); shown on a sandbox.
                    liveSyncOperatorId={effectiveTopId === tourOperator.id ? tourOperator.id : null}
                    onLiveSynced={getTourOperatorUpdateData}
                    formHasChanges={hasFormChanges}
                  />
                </Grid>
              </TabPanel>

              {/* ==============================
                  Tab 4 - Reviews
                  ============================== */}
              <TabPanel value={tabValue} index={TAB_REVIEWS} buttonBoxSpacing>
                <Grid container direction="row" p={2} spacing={2} className={classes.sectionCard}>
                  <Grid item xs={12}>
                    <Typography variant="h6" fontSize={20}>Reviews</Typography>
                  </Grid>
                  <Grid item xs={12} mt={1} mb={-1}>
                    <Typography fontWeight={500}>Review</Typography>
                  </Grid>
                  <Grid item xs={12} container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12} sm={6} lg={3}>
                      <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Review Source" fullWidth value={top.review?.name || ''} name="review_name" onChange={handleReviewChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={6}>
                      <TextField
                        disabled={!hasWritePerm}
                        variant="filled"
                        size="small"
                        label="Review Source Link"
                        required
                        fullWidth
                        value={top.review?.url || ''}
                        name="review_link"
                        onChange={handleReviewChange}
                        InputLabelProps={{ shrink: true }}
                        onBlur={checkValidURL}
                        error={clientErrors.nonValidUrl.review}
                        helperText={clientErrors.nonValidUrl.review && 'Please use a valid URL'}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} mt={1} mb={-1}>
                    <Typography fontWeight={500}>Photo Sharing</Typography>
                  </Grid>
                  <Grid item xs={12} container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={3}>
                      <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Photo Sharing Source" fullWidth value={top.photo_sharing?.name || ''} name="photo_sharing_name" onChange={handleReviewChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        disabled={!hasWritePerm}
                        error={clientErrors.nonValidUrl.photo_sharing}
                        variant="filled"
                        size="small"
                        label="Photo Sharing Link"
                        required
                        fullWidth
                        value={top.photo_sharing?.url || ''}
                        name="photo_sharing_link"
                        onChange={handleReviewChange}
                        InputLabelProps={{ shrink: true }}
                        onBlur={checkValidURL}
                        helperText={clientErrors.nonValidUrl.photo_sharing && 'Please use a valid URL'}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} mt={1}>
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox />}
                        label="Show company title on review page"
                        checked={top.config?.show_company_title !== false}
                        disabled={!hasWritePerm}
                        onChange={(ev) => onSubfieldChange('config.show_company_title', ev.target.checked)}
                      />
                    </FormGroup>
                  </Grid>

                  {hasPerm('reviews', 'read') && (
                    <>
                      <Grid item xs={12} mt={2}>
                        <Typography variant="h6" fontSize={20}>Review feedback templates</Typography>
                      </Grid>
                      <Grid item xs={12} mt={0}>
                        <Typography variant="h6" fontSize={16}>Review page</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography fontWeight={500}>Review Module</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl disabled={!hasReviewsWritePerm}>
                          <RadioGroup value={String(review_rating_system)} name="review_rating_system" row onChange={onReviewRatingSystemChange}>
                            <FormControlLabel value="5" control={<Radio />} label="Smiley rating (1-5 score)" />
                            <FormControlLabel value="10" control={<Radio />} label="NPS (1-10 score)" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <LocalizedTextSetField disabled={!hasReviewsWritePerm} variant="filled" size="small" label="Review page title" name="review_page_title" value={top.review_page_title} fullWidth onChange={onLocalizedTextSetChange} />
                      </Grid>
                      <Grid item xs={12}>
                        <LocalizedTextSetField disabled={!hasReviewsWritePerm} variant="filled" size="small" label="Review page text" name="review_page_text" value={top.review_page_text} fullWidth onChange={onLocalizedTextSetChange} />
                      </Grid>
                      <Grid item xs={12}>
                        <LocalizedTextSetField disabled={!hasReviewsWritePerm} variant="filled" size="small" label="Review page button text" name="review_send_button" value={top.review_send_button} fullWidth onChange={onLocalizedTextSetChange} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} style={{ maxWidth: '200px' }} container alignItems="center">
                        <Grid item xs={12}>
                          <FormControl fullWidth variant="filled" size="small" disabled={!hasReviewsWritePerm}>
                            <InputLabel>Positive Review Threshold</InputLabel>
                            <Select
                              name="reviews_low_high_threshold"
                              value={review_threshold}
                              onChange={onReviewLowHighThresholdChange}
                              disabled={!hasReviewsWritePerm}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                                  {renderRatingValue(selected)}
                                </Box>
                              )}
                            >
                              {ratingOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                                    {renderRatingValue(option.value)}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={6} md={8} container alignContent="center">
                        {review_rating_system === 5 && review_threshold === 5 ? (
                          <Typography variant="body2">
                            Ratings of {review_threshold} will be considered as positive feedback
                          </Typography>
                        ) : review_rating_system === 10 ? (
                          <Typography variant="body2">
                            Ratings from {review_threshold} to 10 will be considered as positive feedback
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            Ratings bigger or equal to {review_threshold} will be considered as positive feedback
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl disabled={!hasReviewsWritePerm}>
                          <RadioGroup value={top.config?.review_widget_type || "0"} name="review_widget_type" row onChange={onReviewWidgetTypeChange}>
                            <FormControlLabel value="0" control={<Radio />} label="Simple" />
                            <FormControlLabel value="1" control={<Radio />} label="Advanced" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>{review_widget_type === 1 ? 'Positive feedback pages' : 'Positive feedback page'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl disabled={!hasReviewsWritePerm}>
                          <RadioGroup row name="positive_review_direct_link" value={top.config?.positive_review_direct_link ? 'link' : 'page'}
                            onChange={(ev) => onSubfieldChange('config.positive_review_direct_link', ev.target.value === 'link')}>
                            <FormControlLabel value="page" control={<Radio />} label="Review Page" />
                            <FormControlLabel value="link" control={<Radio />} label="Direct to link" />
                          </RadioGroup>
                          <FormHelperText>
                            {positiveFeedbackHelp(!!top.config?.positive_review_direct_link, review_widget_type === 1)}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl disabled={!hasReviewsWritePerm}>
                          <Typography variant="body2" color="text.secondary">Review link</Typography>
                          <RadioGroup row name="positive_review_url_source" value={String(+top.config?.positive_review_url_source || 0)}
                            onChange={(ev) => onSubfieldChange('config.positive_review_url_source', +ev.target.value)}>
                            <FormControlLabel value="0" control={<Radio />} label="Positive feedback target (set on each trip)" />
                            <FormControlLabel value="1" control={<Radio />} label="Custom URL" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      {+top.config?.positive_review_url_source === 1 && (
                        <Grid item xs={12}>
                          <LocalizedTextSetField disabled={!hasReviewsWritePerm} variant="filled" size="small" label="Custom review URL" name="positive_review_url_template" value={top.positive_review_url_template} fullWidth onChange={onLocalizedTextSetChange} />
                          <FormHelperText>
                            Django template that must produce the review URL. It has the same variables as the review page texts, e.g.{' '}
                            <code>participant.user.ota.name</code>, <code>participant.tour.id</code>, <code>participant.custom_id</code>. Example:{' '}
                            <code>{'{% if participant.user.ota.name == "GetYourGuide" %}https://… {% else %}https://… {% endif %}'}</code>.
                            Line breaks and spaces are ignored. An empty result means no review link for that guest.
                          </FormHelperText>
                        </Grid>
                      )}
                      {review_widget_type === 0 && (
                        <>
                          <Grid item xs={12}>
                            <LocalizedTextSetField disabled={!hasWritePerm} variant="filled" size="small" label="Positive feedback page: title" name="positive_review_title" value={top.positive_review_title} fullWidth onChange={onLocalizedTextSetChange} />
                          </Grid>
                          <Grid item xs={12}>
                            <LocalizedTextSetField disabled={!hasWritePerm} variant="filled" size="small" label="Positive feedback page: text" name="positive_review_feedback" value={top.positive_review_feedback} fullWidth onChange={onLocalizedTextSetChange} />
                          </Grid>
                          <Grid item xs={12}>
                            <LocalizedTextSetField disabled={!hasWritePerm} variant="filled" size="small" label="Positive feedback page: button text" name="positive_send_button" value={top.positive_send_button} fullWidth onChange={onLocalizedTextSetChange} />
                          </Grid>
                          <Grid item xs={12} mt={2}>
                            <Typography fontWeight={500}>Negative feedback page</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <LocalizedTextSetField disabled={!hasWritePerm} variant="filled" size="small" label="Negative feedback page: title" name="negative_review_title" value={top.negative_review_title} fullWidth onChange={onLocalizedTextSetChange} />
                          </Grid>
                          <Grid item xs={12}>
                            <LocalizedTextSetField disabled={!hasWritePerm} variant="filled" size="small" label="Negative feedback page: text" name="negative_review_feedback" value={top.negative_review_feedback} fullWidth onChange={onLocalizedTextSetChange} />
                          </Grid>
                          <Grid item xs={12}>
                            <LocalizedTextSetField disabled={!hasWritePerm} variant="filled" size="small" label="Negative feedback page: comment placeholder" name="negative_comment_placeholder" value={top.negative_comment_placeholder} fullWidth onChange={onLocalizedTextSetChange} />
                          </Grid>
                          <Grid item xs={12}>
                            <LocalizedTextSetField disabled={!hasWritePerm} variant="filled" size="small" label="Negative feedback page: button text" name="negative_send_button" value={top.negative_send_button} fullWidth onChange={onLocalizedTextSetChange} />
                          </Grid>
                          <Grid item xs={12}>
                            <LocalizedTextSetField disabled={!hasWritePerm} variant="filled" size="small" label="Negative feedback conclusion text" name="negative_review_conclusion" value={top.negative_review_conclusion} fullWidth onChange={onLocalizedTextSetChange} />
                          </Grid>
                        </>
                      )}
                      {review_widget_type === 1 && (
                        <Grid item xs={12}>
                          <AdvanecdReviewWidget />
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </TabPanel>

              {/* ==============================
                  Tab 5 - Chat
                  ============================== */}
              <TabPanel value={tabValue} index={TAB_CHAT} buttonBoxSpacing>
                <Grid container direction="row" p={2} spacing={2} className={classes.sectionCard}>
                  <Grid item xs={12}>
                    <Typography variant="h6" fontSize={20}>Chat Notifications</Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} container direction="row">
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox />}
                        label="Enable notification sound (when tab is in background)"
                        checked={!!top.config?.background_dashboard_chat_notification_sound_enabled}
                        name="background_dashboard_chat_notification_sound_enabled"
                        disabled={!hasWritePerm}
                        onChange={(ev) => onSubfieldChange('config.background_dashboard_chat_notification_sound_enabled', ev.target.checked)}
                      />
                      <FormControlLabel
                        control={<Checkbox />}
                        label="Enable notification sound (when tab is in foreground)"
                        checked={!!top.config?.foreground_dashboard_chat_notification_sound_enabled}
                        name="foreground_dashboard_chat_notification_sound_enabled"
                        disabled={!hasWritePerm}
                        onChange={(ev) => onSubfieldChange('config.foreground_dashboard_chat_notification_sound_enabled', ev.target.checked)}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </TabPanel>

          </div>
        </LocalizationProvider>
      </div>

      {/* Save / Cancel — always visible */}
      <Grid item xs={12} className={classes.buttonPanel} container alignItems="center" py={2} px={2}>
        <FormActionButtons
          mode="edit"
          size="medium"
          sx={{ width: '100%' }}
          onCancel={onCancel}
          cancelDisabled={syncActive || !hasFormChanges}
          onSubmit={onSave}
          submitDisabled={Boolean(
            !hasWritePerm ||
              syncActive ||
              !hasFormChanges ||
              clientErrors.bookingUrl ||
              clientErrors.websiteUrl ||
              clientErrors.nonValidUrl.review ||
              clientErrors.nonValidUrl.photo_sharing ||
              clientErrors.operatorEmail ||
              (!officeHoursValid && officeHoursChanged)
          )}
        />
      </Grid>
    </FormLicenseeContext.Provider>
  );
};

export default GeneralSettingsTabs;
